import React, { Component } from 'react'

import { isValid } from 'date-fns'
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  CalendarIcon,
  Copy,
  Import,
  Info,
  Settings2Icon,
} from 'lucide-react'
import { makeObservable } from 'mobx'
import { observer } from 'mobx-react'

import { SharingMode } from '~/client/graph'
import { ExternalCalendarDateRange } from '~/client/src/desktop/enums/ExternalCalendarDateRange'
import { ExternalCalendarWorkflowType } from '~/client/src/desktop/enums/ExternalCalendarWorkflowType'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Field,
  FieldError,
} from '~/client/src/shared/components/shadcn-ui/field'
import { Input } from '~/client/src/shared/components/shadcn-ui/input'
import { Separator } from '~/client/src/shared/components/shadcn-ui/separator'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import CalendarLinkInputStore from './CalendarLinkInput.store'
import CalendarDatePicker from './components/CalendarDatePicker'
import CalendarOptionsPicker from './components/CalendarOptionsPicker'
import CalendarSubscriptions from './components/CalendarSubscriptions'

interface IProps {
  store: CalendarLinkInputStore
}

@observer
export default class CalendarLinkInput extends Component<IProps> {
  public constructor(props: IProps) {
    super(props)

    makeObservable(this)
  }

  public componentDidMount() {
    this.store.initialize()
  }

  public render(): JSX.Element {
    const {
      isDisableAccessDialogShown,
      setIsDisableAccessDialogShown,
      isRemoveSubscriptionDialogShown,
      setIsRemoveSubscriptionDialogShown,
      subscriptionIdToRemove,
      unsubscribe,
    } = this.store

    return (
      <>
        <ConfirmDialog
          isOpen={isDisableAccessDialogShown}
          title={Localization.translator.areYouSureYouWantToDisableAccess}
          question={
            Localization.translator.allExternalProjectsWillLosePermission
          }
          doneTitle={Localization.translator.disableAccess}
          useRedConfirmationButton
          onCancelClicked={() => setIsDisableAccessDialogShown(false, false)}
          onDoneClicked={() => setIsDisableAccessDialogShown(false, true)}
        />
        <ConfirmDialog
          isOpen={isRemoveSubscriptionDialogShown}
          title={Localization.translator.areYouSureYouWantToDeleteSubscription}
          question={
            Localization.translator
              .inThisCaseYouWillNotBeAbleToSeeAnyWorkflowsFromThisProject
          }
          doneTitle={Localization.translator.deleteSubscription}
          useRedConfirmationButton
          onCancelClicked={() =>
            setIsRemoveSubscriptionDialogShown(false, null)
          }
          onDoneClicked={() => unsubscribe(subscriptionIdToRemove)}
        />
        <div className="tw:flex tw:items-start tw:gap-5 tw:text-body-sm tw:text-foreground">
          <div className="tw:flex tw:w-[700px] tw:shrink-0 tw:flex-col tw:rounded-xl tw:border tw:border-solid tw:border-border tw:p-2.5">
            {this.renderLinkInput(
              this.store.stxLinkInputValue,
              Localization.translator.shareToExternalProject,
              () => this.store.copyToClipboardSTX(),
              <ArrowUpRightIcon size={14} className="tw:shrink-0" />,
              Localization.translator.generateShareableLink,
              true,
            )}
            <Separator className="tw:my-5" />
            {this.renderLinkInput(
              this.store.inputValue,
              Localization.translator.shareIcalCalendarLink,
              this.store.copyToClipboard,
              <CalendarIcon size={14} className="tw:shrink-0" />,
              Localization.translator.generateShareableCalendarLink,
              true,
            )}
            <Separator className="tw:mt-5" />
            {this.renderSharingSettings()}
          </div>
          <div className="tw:flex tw:w-[700px] tw:shrink-0 tw:flex-col tw:rounded-xl tw:border tw:border-solid tw:border-border tw:p-5">
            {this.renderImportInput()}
          </div>
        </div>
      </>
    )
  }

  private renderSharingSettings(): JSX.Element {
    const {
      calendarFormSharingMode,
      calendarDeliverySharingMode,
      isEnabled,
      changeDeliverySharingDateRange,
      deliverySharingTo,
      deliverySharingFrom,
      formSharingFrom,
      formSharingTo,
      changeFormSharingDateRange,
      currentFormSharingEndOption,
      currentDeliverySharingEndOption,
    } = this.store

    const shouldRenderDeliveryDatePickers =
      calendarDeliverySharingMode === SharingMode.AllInDateRange && isEnabled
    const shouldRenderFormDatePickers =
      calendarFormSharingMode === SharingMode.AllInDateRange && isEnabled

    return (
      <div className="tw:flex tw:flex-col tw:p-2.5">
        {this.renderSectionHeader(
          Localization.translator.sharingSettings,
          <Settings2Icon size={14} className="tw:shrink-0" />,
          null,
          true,
        )}
        {this.renderFormSharingOptions()}
        {shouldRenderFormDatePickers && (
          <CalendarDatePicker
            store={this.store}
            openFrom={ExternalCalendarDateRange.FORM_FROM}
            openTo={ExternalCalendarDateRange.FORM_TO}
            from={formSharingFrom}
            to={formSharingTo}
            handler={changeFormSharingDateRange}
            endOption={currentFormSharingEndOption}
          />
        )}
        {this.renderDeliverySharingOptions()}
        {shouldRenderDeliveryDatePickers && (
          <CalendarDatePicker
            store={this.store}
            openFrom={ExternalCalendarDateRange.DELIVERY_FROM}
            openTo={ExternalCalendarDateRange.DELIVERY_TO}
            from={deliverySharingFrom}
            to={deliverySharingTo}
            handler={changeDeliverySharingDateRange}
            endOption={currentDeliverySharingEndOption}
          />
        )}
      </div>
    )
  }

  private renderSectionHeader(
    headerText: string,
    icon: JSX.Element,
    toolTipText: string,
    shouldHideTooltip: boolean = false,
  ): JSX.Element {
    return (
      <div className="tw:mb-5 tw:flex tw:items-center">
        {icon}
        <span className="tw:ml-2.5 tw:whitespace-nowrap tw:text-label-md-caps-prominent tw:uppercase">
          {headerText}
        </span>
        {!shouldHideTooltip && (
          <TooltipWrapper
            content={toolTipText}
            className="tw:ml-1 tw:flex tw:items-center"
          >
            <Info size={14} className="tw:block tw:text-muted-foreground" />
          </TooltipWrapper>
        )}
      </div>
    )
  }

  private renderLinkInput(
    link: string,
    headerText: string,
    copyToClipboard: () => void,
    icon: JSX.Element,
    toolTipText: string,
    shouldRenderToggle: boolean = false,
  ): JSX.Element {
    return (
      <div className="tw:flex tw:flex-col tw:p-2.5">
        {this.renderSectionHeader(headerText, icon, toolTipText)}
        <div className="tw:ml-5 tw:flex tw:items-center">
          <Input
            value={link}
            readOnly
            disabled={!this.store.isEnabled}
            aria-label={headerText}
            className="tw:w-[380px]"
          />
          <Button
            variant="primary-outline"
            className="tw:ml-2.5"
            onClick={copyToClipboard}
          >
            <Copy data-icon="inline-start" />
            {Localization.translator.copyLink}
          </Button>
          {shouldRenderToggle && this.renderCalendarSwitch()}
        </div>
      </div>
    )
  }

  private renderImportInput(): JSX.Element {
    const {
      isLoading,
      importInputValue,
      setImportInputValue,
      subscribe,
      importInputError,
      isImportUrlValid,
    } = this.store

    return (
      <>
        {this.renderSectionHeader(
          Localization.translator.subscribeToExternalProjects,
          <ArrowDownLeftIcon size={14} className="tw:shrink-0" />,
          Localization.translator.subscribeShareableLink,
        )}
        <div className="tw:flex tw:items-start tw:px-7.5">
          <Field className="tw:w-[200px] tw:grow">
            <Input
              placeholder={Localization.translator.pastUrlFromOtherProject}
              aria-label={Localization.translator.pastUrlFromOtherProject}
              onChange={setImportInputValue}
              value={importInputValue}
              aria-invalid={!!importInputError}
            />
            <FieldError>{importInputError}</FieldError>
          </Field>
          <Button
            variant="secondary"
            className="tw:ml-2.5"
            onClick={subscribe.bind(null, importInputValue)}
            disabled={isLoading || !isImportUrlValid}
          >
            {isLoading ? (
              <Spinner size="xs" />
            ) : (
              <>
                <Import data-icon="inline-start" />
                {Localization.translator.import}
              </>
            )}
          </Button>
        </div>
        <CalendarSubscriptions store={this.store} />
      </>
    )
  }

  private renderCalendarSwitch(): JSX.Element {
    const { isEnabled, isSwitchDisabled, toggleDisableAccessDialog } =
      this.store

    return (
      <div className="tw:mb-1 tw:flex tw:items-center tw:gap-1.5 tw:ml-2.5">
        <TooltipWrapper
          content={
            <div className="tw:flex tw:flex-col tw:gap-2">
              <span>{Localization.translator.ifTheProjectIsEnabled}</span>
              <span>{Localization.translator.ifTheProjectIsDisabled}</span>
            </div>
          }
          className="tw:flex tw:items-center"
        >
          <Info size={18} className="tw:block tw:text-muted-foreground" />
        </TooltipWrapper>
        <span>
          {isEnabled
            ? Localization.translator.enabled
            : Localization.translator.disabled}
        </span>
        <Switch
          checked={isEnabled}
          onCheckedChange={toggleDisableAccessDialog}
          disabled={isSwitchDisabled}
        />
      </div>
    )
  }

  private renderDeliverySharingOptions(): JSX.Element {
    const { deliverySharingFrom, deliverySharingTo } = this.store

    const parsedFrom = new Date(deliverySharingFrom)
    const parsedTo = new Date(deliverySharingTo)

    if (!isValid(parsedFrom) || !isValid(parsedTo)) {
      return null
    }

    return (
      <CalendarOptionsPicker
        store={this.store}
        workflowType={ExternalCalendarWorkflowType.DELIVERY}
        fromRulesLabelText={Localization.translator.deliveryNotifications}
        allInDateRangeLabelText={
          Localization.translator.shareAllDeliveriesWithinSpecificDate
        }
      />
    )
  }

  private renderFormSharingOptions(): JSX.Element {
    const { formSharingFrom, formSharingTo } = this.store

    const parsedFrom = new Date(formSharingFrom)
    const parsedTo = new Date(formSharingTo)

    if (!isValid(parsedFrom) || !isValid(parsedTo)) {
      return null
    }

    return (
      <CalendarOptionsPicker
        store={this.store}
        workflowType={ExternalCalendarWorkflowType.FORM}
        fromRulesLabelText={Localization.translator.formNotifications}
        allInDateRangeLabelText={
          Localization.translator.shareAllFormsWithinSpecificDate
        }
      />
    )
  }

  private get store(): CalendarLinkInputStore {
    return this.props.store
  }
}
